import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

const navGroups = [
  { name: 'HOME', href: '/' },
  {
    name: 'ABOUT',
    children: [
      { name: 'About Us', href: '/about' },
      { name: 'Why We Do', href: '/why-we-do' },
      { name: 'Visit Us', href: '/visit' },
      { name: 'Statement of Faith', href: '/statement-of-faith' },
    ],
  },
  {
    name: 'RESOURCES',
    children: [
      { name: 'Sermons', href: '/sermons' },
      { name: 'Daily Devotions', href: '/devotions' },
      { name: 'Bible Studies', href: '/bible-studies' },
      { name: 'Song Library', href: '/songs/English' },
      { name: 'Service Playlists', href: '/playlists' },
      { name: 'Book Library', href: '/library' },
      { name: 'Blog', href: '/blog' },
    ],
  },
  {
    name: 'CONNECT',
    children: [
      { name: 'Prayer Requests', href: '/prayer' },
      { name: 'Outreach', href: '/outreach' },
      { name: 'Contact Us', href: '/contact' },
    ],
  },
];

const DesktopDropdown = ({ group, isActive, closeAll }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timeout = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleEnter = () => {
    clearTimeout(timeout.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeout.current = setTimeout(() => setOpen(false), 150);
  };

  const groupActive = group.children?.some((c) => isActive(c.href));

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        data-testid={`nav-${group.name.toLowerCase()}-dropdown`}
        onClick={() => setOpen((p) => !p)}
        className={`flex items-center gap-1 px-3 py-2 text-xs font-semibold transition-colors duration-200 font-sans ${
          groupActive
            ? 'text-[#f17b21] border-b-2 border-[#f17b21]'
            : 'text-gray-700 hover:text-[#f17b21]'
        }`}
      >
        {group.name}
        <ChevronDown
          className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {group.children.map((child) => (
            <Link
              key={child.href}
              to={child.href}
              data-testid={`nav-link-${child.href.replace(/\//g, '-').slice(1)}`}
              onClick={() => {
                setOpen(false);
                closeAll();
              }}
              className={`block px-4 py-2.5 text-sm transition-colors duration-150 font-sans ${
                isActive(child.href)
                  ? 'text-[#f17b21] bg-orange-50 font-semibold'
                  : 'text-gray-700 hover:text-[#f17b21] hover:bg-gray-50'
              }`}
            >
              {child.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setExpandedGroup(null);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const toggleMobileGroup = (name) => {
    setExpandedGroup((prev) => (prev === name ? null : name));
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-50" data-testid="main-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            onClick={scrollToTop}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            data-testid="header-logo"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_f1a5c9b5-8f1b-4d34-88be-faeda192a0ed/artifacts/8ox9chkc_2.png"
              alt="El Bethel Church"
              className="w-10 h-10 lg:w-12 lg:h-12"
            />
            <div className="flex flex-col text-xs lg:text-sm font-bold text-gray-800 font-display leading-tight">
              <span>EL BETHEL</span>
              <span className="text-[10px] lg:text-xs text-gray-600 font-sans font-normal">
                CHURCH
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navGroups.map((group) =>
              group.children ? (
                <DesktopDropdown
                  key={group.name}
                  group={group}
                  isActive={isActive}
                  closeAll={() => {}}
                />
              ) : (
                <Link
                  key={group.name}
                  to={group.href}
                  onClick={scrollToTop}
                  data-testid={`nav-link-home`}
                  className={`px-3 py-2 text-xs font-semibold transition-colors duration-200 font-sans ${
                    isActive(group.href)
                      ? 'text-[#f17b21] border-b-2 border-[#f17b21]'
                      : 'text-gray-700 hover:text-[#f17b21]'
                  }`}
                >
                  {group.name}
                </Link>
              )
            )}

            {/* Give CTA */}
            <Link
              to="/give"
              data-testid="nav-give-btn"
              className={`ml-2 px-5 py-2 text-xs font-semibold rounded-lg transition-all duration-200 ${
                isActive('/give')
                  ? 'bg-[#d86919] text-white'
                  : 'bg-[#f17b21] hover:bg-[#d86919] text-white'
              }`}
            >
              GIVE
            </Link>

            {/* Online Ministry */}
            <a
              href="https://elbethel.online"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-online-ministry"
              className="ml-2 px-4 py-2 border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white text-xs font-semibold rounded-lg transition-all duration-200 inline-flex items-center gap-1"
            >
              ONLINE
              <ExternalLink className="w-3 h-3" />
            </a>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-[#f17b21] transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav
            className="lg:hidden py-2 border-t border-gray-200 max-h-[calc(100vh-4rem)] overflow-y-auto"
            data-testid="mobile-nav"
          >
            {navGroups.map((group) =>
              group.children ? (
                <div key={group.name}>
                  <button
                    onClick={() => toggleMobileGroup(group.name)}
                    data-testid={`mobile-nav-${group.name.toLowerCase()}`}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold transition-colors font-sans ${
                      group.children.some((c) => isActive(c.href))
                        ? 'text-[#f17b21] bg-orange-50'
                        : 'text-gray-700 hover:text-[#f17b21] hover:bg-gray-50'
                    }`}
                  >
                    {group.name}
                    <ChevronRight
                      className={`w-4 h-4 transition-transform duration-200 ${
                        expandedGroup === group.name ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {expandedGroup === group.name && (
                    <div className="bg-gray-50 border-l-4 border-[#f17b21]/30">
                      {group.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => {
                            setIsMenuOpen(false);
                            scrollToTop();
                          }}
                          data-testid={`mobile-link-${child.href.replace(/\//g, '-').slice(1)}`}
                          className={`block pl-8 pr-4 py-2.5 text-sm transition-colors font-sans ${
                            isActive(child.href)
                              ? 'text-[#f17b21] font-semibold'
                              : 'text-gray-600 hover:text-[#f17b21]'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={group.name}
                  to={group.href}
                  onClick={() => {
                    setIsMenuOpen(false);
                    scrollToTop();
                  }}
                  data-testid={`mobile-link-home`}
                  className={`block px-4 py-3 text-sm font-semibold transition-colors font-sans ${
                    isActive(group.href)
                      ? 'text-[#f17b21] bg-orange-50'
                      : 'text-gray-700 hover:text-[#f17b21] hover:bg-gray-50'
                  }`}
                >
                  {group.name}
                </Link>
              )
            )}

            {/* Give CTA mobile */}
            <Link
              to="/give"
              onClick={() => {
                setIsMenuOpen(false);
                scrollToTop();
              }}
              data-testid="mobile-give-btn"
              className="block mx-4 mt-3 px-4 py-3 bg-[#f17b21] hover:bg-[#d86919] text-white text-sm font-semibold rounded-lg transition-all text-center"
            >
              GIVE
            </Link>
            <a
              href="https://elbethel.online"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
              data-testid="mobile-online-ministry"
              className="block mx-4 mt-2 mb-3 px-4 py-3 border-2 border-[#f17b21] text-[#f17b21] text-sm font-semibold rounded-lg transition-all text-center"
            >
              VISIT ELBETHEL.ONLINE
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
